import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getRestaurantReportSettings, updateRestaurantReportSettings, ReportSettings } from '../../../services/restaurant/restaurantService';
import { fetchRevenueItemNamesBySourceType } from '../../../services/costPrice/costPriceService';
import { DEFAULT_COST_TARGET, resolveCostTargets } from '../../../utils/costTargets';
import { DEFAULT_WASTE_PERCENTAGE, resolveWastePercentage } from '../../../utils/wasteUtils';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';

interface ReportSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  onSaved: () => void;
}

const ReportSettingsModal: React.FC<ReportSettingsModalProps> = ({
  isOpen,
  onClose,
  restaurantId,
  onSaved,
}) => {
  const { t } = useTranslation();
  const setReportSettingsInStore = useRestaurantStore(s => s.setReportSettings);
  const [settings, setSettings] = useState<ReportSettings>({
    trackedDishes: { food: [], drinks: [] },
    sections: {},
    costTargets: { food: DEFAULT_COST_TARGET, drinks: DEFAULT_COST_TARGET, overall: DEFAULT_COST_TARGET },
    wastePercentage: DEFAULT_WASTE_PERCENTAGE,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foodDishSearch, setFoodDishSearch] = useState('');
  const [drinksDishSearch, setDrinksDishSearch] = useState('');
  const [foodSuggestions, setFoodSuggestions] = useState<string[]>([]);
  const [drinksSuggestions, setDrinksSuggestions] = useState<string[]>([]);
  const [showFoodSuggestions, setShowFoodSuggestions] = useState(false);
  const [showDrinksSuggestions, setShowDrinksSuggestions] = useState(false);
  const foodInputRef = useRef<HTMLInputElement>(null);
  const drinksInputRef = useRef<HTMLInputElement>(null);
  const foodDropdownRef = useRef<HTMLDivElement>(null);
  const drinksDropdownRef = useRef<HTMLDivElement>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRestaurantReportSettings(restaurantId);
      // Ensure sections and costTargets objects exist with defaults
      setSettings({
        ...data,
        sections: data.sections || {},
        costTargets: resolveCostTargets(data.costTargets),
        wastePercentage: resolveWastePercentage(data.wastePercentage),
      });
    } catch (err: any) {
      setError(err.message || t('profile.reportSettings.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [restaurantId, t]);

  const loadSuggestions = useCallback(async () => {
    try {
      // Get revenue items filtered by source type
      const [foodItems, drinksItems] = await Promise.all([
        fetchRevenueItemNamesBySourceType(restaurantId, 'FOOD'),
        fetchRevenueItemNamesBySourceType(restaurantId, 'DRINKS')
      ]);
      setFoodSuggestions(foodItems);
      setDrinksSuggestions(drinksItems);
    } catch (err) {
      console.error('Failed to load dish suggestions', err);
      // Fallback to empty arrays if filtering fails
      setFoodSuggestions([]);
      setDrinksSuggestions([]);
    }
  }, [restaurantId]);

  // Load settings when modal opens
  useEffect(() => {
    if (isOpen && restaurantId) {
      loadSettings();
      loadSuggestions();
    }
  }, [isOpen, restaurantId, loadSettings, loadSuggestions]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (foodDropdownRef.current && !foodDropdownRef.current.contains(event.target as Node)) {
        setShowFoodSuggestions(false);
      }
      if (drinksDropdownRef.current && !drinksDropdownRef.current.contains(event.target as Node)) {
        setShowDrinksSuggestions(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAddFoodDish = (dishName: string) => {
    if (settings.trackedDishes.food.length >= 10) {
      setError(t('profile.reportSettings.maxFoodDishes'));
      return;
    }
    if (!settings.trackedDishes.food.includes(dishName)) {
      setSettings({
        ...settings,
        trackedDishes: {
          ...settings.trackedDishes,
          food: [...settings.trackedDishes.food, dishName]
        }
      });
    }
    setFoodDishSearch('');
    setShowFoodSuggestions(false);
  };

  const handleAddDrinksDish = (dishName: string) => {
    if (settings.trackedDishes.drinks.length >= 10) {
      setError(t('profile.reportSettings.maxDrinksDishes'));
      return;
    }
    if (!settings.trackedDishes.drinks.includes(dishName)) {
      setSettings({
        ...settings,
        trackedDishes: {
          ...settings.trackedDishes,
          drinks: [...settings.trackedDishes.drinks, dishName]
        }
      });
    }
    setDrinksDishSearch('');
    setShowDrinksSuggestions(false);
  };

  const handleRemoveFoodDish = (dishName: string) => {
    setSettings({
      ...settings,
      trackedDishes: {
        ...settings.trackedDishes,
        food: settings.trackedDishes.food.filter(d => d !== dishName)
      }
    });
  };

  const handleRemoveDrinksDish = (dishName: string) => {
    setSettings({
      ...settings,
      trackedDishes: {
        ...settings.trackedDishes,
        drinks: settings.trackedDishes.drinks.filter(d => d !== dishName)
      }
    });
  };

  const handleCostTargetChange = (key: 'food' | 'drinks' | 'overall', raw: string) => {
    const parsed = parseFloat(raw);
    const next = Number.isFinite(parsed) ? parsed : 0;
    setSettings(prev => {
      const current = resolveCostTargets(prev.costTargets);
      return {
        ...prev,
        costTargets: { ...current, [key]: next },
      };
    });
  };

  const handleWastePercentageChange = (raw: string) => {
    const parsed = parseFloat(raw);
    const next = Number.isFinite(parsed) ? parsed : 0;
    setSettings(prev => ({ ...prev, wastePercentage: next }));
  };

  const handleToggleSection = (sectionName: keyof NonNullable<ReportSettings['sections']>) => {
    setSettings({
      ...settings,
      sections: {
        ...settings.sections,
        [sectionName]: settings.sections?.[sectionName] !== false ? false : true
      }
    });
  };

  const handleSave = async () => {
    const valid = (n: number | undefined) => typeof n === 'number' && n > 0 && n <= 100;
    if (
      !valid(settings.costTargets?.food) ||
      !valid(settings.costTargets?.drinks) ||
      !valid(settings.costTargets?.overall)
    ) {
      setError(t('profile.reportSettings.costTargets.invalid'));
      return;
    }
    // Waste allows 0 (>= 0), unlike cost targets (> 0).
    const wasteValid = (n: number | undefined) => typeof n === 'number' && n >= 0 && n <= 100;
    if (!wasteValid(settings.wastePercentage)) {
      setError(t('profile.reportSettings.waste.invalid'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateRestaurantReportSettings(restaurantId, settings);
      // Update the shared cache so every consumer (profile chips, cost-price
      // page, dashboard budget, subrecipe modals) reflects the edit with no refetch.
      setReportSettingsInStore(restaurantId, settings);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || t('profile.reportSettings.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const filteredFoodSuggestions = foodSuggestions.filter(
    item => item.toLowerCase().includes(foodDishSearch.toLowerCase()) &&
            !settings.trackedDishes.food.includes(item)
  ).slice(0, 10);

  const filteredDrinksSuggestions = drinksSuggestions.filter(
    item => item.toLowerCase().includes(drinksDishSearch.toLowerCase()) &&
            !settings.trackedDishes.drinks.includes(item)
  ).slice(0, 10);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black dark:bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-50">
            {t('profile.restaurantSettings.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-zinc-600 dark:text-zinc-400">
              {t('common.loading')}
            </div>
          ) : (
            <>
              {/* Report Settings section */}
              <div className="border-b border-zinc-200 dark:border-zinc-700 pb-2">
                <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">
                  {t('profile.restaurantSettings.reportSectionTitle')}
                </h3>
                <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                  {t('profile.restaurantSettings.reportSectionDescription')}
                </p>
              </div>

              {/* Cost Targets */}
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 mb-1">
                  {t('profile.reportSettings.costTargets.title')}
                </h3>
                <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-3">
                  {t('profile.reportSettings.costTargets.description')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {(['overall', 'food', 'drinks'] as const).map((key) => (
                    <div key={key} className="flex items-center justify-between gap-3 px-3 py-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                      <label htmlFor={`cost-target-${key}`} className="text-sm text-zinc-900 dark:text-zinc-50">
                        {t(`profile.reportSettings.costTargets.${key}`)}
                      </label>
                      <div className="flex items-center gap-1">
                        <input
                          id={`cost-target-${key}`}
                          type="number"
                          min={1}
                          max={100}
                          step={0.5}
                          value={settings.costTargets?.[key] ?? DEFAULT_COST_TARGET}
                          onChange={(e) => handleCostTargetChange(key, e.target.value)}
                          className="w-20 px-2 py-1 text-right border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
                        />
                        <span className="text-sm text-zinc-600 dark:text-zinc-300">%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Default Waste % */}
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 mb-1">
                  {t('profile.reportSettings.waste.title')}
                </h3>
                <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-3">
                  {t('profile.reportSettings.waste.description')}
                </p>
                <div className="flex items-center justify-between gap-3 px-3 py-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg sm:max-w-[12rem]">
                  <label htmlFor="default-waste" className="text-sm text-zinc-900 dark:text-zinc-50">
                    {t('profile.reportSettings.waste.label')}
                  </label>
                  <div className="flex items-center gap-1">
                    <input
                      id="default-waste"
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={settings.wastePercentage ?? DEFAULT_WASTE_PERCENTAGE}
                      onChange={(e) => handleWastePercentageChange(e.target.value)}
                      className="w-20 px-2 py-1 text-right border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
                    />
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">%</span>
                  </div>
                </div>
              </div>

              {/* Report Sections */}
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 mb-1">
                  {t('profile.reportSettings.sections.title')}
                </h3>
                <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-3">
                  {t('profile.reportSettings.sections.description')}
                </p>
                <div className="space-y-2">
                  {[
                    { key: 'snapshotOverview' as const, label: t('profile.reportSettings.sections.snapshotOverview') },
                    { key: 'dailyPerformance' as const, label: t('profile.reportSettings.sections.dailyPerformance') },
                    { key: 'topSellingItems' as const, label: t('profile.reportSettings.sections.topSellingItems') },
                    { key: 'bottomSellingItems' as const, label: t('profile.reportSettings.sections.bottomSellingItems') },
                    { key: 'dishPerformance' as const, label: t('profile.reportSettings.sections.dishPerformance') },
                    { key: 'highestSpendingProducts' as const, label: t('profile.reportSettings.sections.highestSpendingProducts') },
                    { key: 'priceAlerts' as const, label: t('profile.reportSettings.sections.priceAlerts') },
                  ].map((section) => (
                    <div
                      key={section.key}
                      className="flex items-center justify-between px-3 py-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg"
                    >
                      <label
                        htmlFor={`section-${section.key}`}
                        className="text-sm text-zinc-900 dark:text-zinc-50 cursor-pointer flex-1"
                      >
                        {section.label}
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id={`section-${section.key}`}
                          checked={settings.sections?.[section.key] !== false}
                          onChange={() => handleToggleSection(section.key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 dark:peer-focus:ring-accent-dark/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-accent dark:peer-checked:bg-accent-dark"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Food Dishes */}
              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-50 mb-2">
                  {t('profile.reportSettings.foodDishes')}
                </label>
                <div className="relative" ref={foodDropdownRef}>
                  <input
                    ref={foodInputRef}
                    type="text"
                    value={foodDishSearch}
                    onChange={(e) => {
                      setFoodDishSearch(e.target.value);
                      setShowFoodSuggestions(true);
                    }}
                    onFocus={() => setShowFoodSuggestions(true)}
                    placeholder={t('profile.reportSettings.searchFoodDishes')}
                    className="w-full px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
                  />
                  {showFoodSuggestions && filteredFoodSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredFoodSuggestions.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => handleAddFoodDish(item)}
                          className="w-full text-left px-4 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-50"
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-3 space-y-2">
                  {settings.trackedDishes.food.map((dish) => (
                    <div
                      key={dish}
                      className="flex items-center justify-between px-4 py-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg"
                    >
                      <span className="text-sm text-zinc-900 dark:text-zinc-50">{dish}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFoodDish(dish)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {settings.trackedDishes.food.length === 0 && (
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
                      {t('profile.reportSettings.noFoodDishesSelected')}
                    </p>
                  )}
                </div>
              </div>

              {/* Drinks Dishes */}
              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-50 mb-2">
                  {t('profile.reportSettings.drinksDishes')}
                </label>
                <div className="relative" ref={drinksDropdownRef}>
                  <input
                    ref={drinksInputRef}
                    type="text"
                    value={drinksDishSearch}
                    onChange={(e) => {
                      setDrinksDishSearch(e.target.value);
                      setShowDrinksSuggestions(true);
                    }}
                    onFocus={() => setShowDrinksSuggestions(true)}
                    placeholder={t('profile.reportSettings.searchDrinksDishes')}
                    className="w-full px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
                  />
                  {showDrinksSuggestions && filteredDrinksSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredDrinksSuggestions.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => handleAddDrinksDish(item)}
                          className="w-full text-left px-4 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-50"
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-3 space-y-2">
                  {settings.trackedDishes.drinks.map((dish) => (
                    <div
                      key={dish}
                      className="flex items-center justify-between px-4 py-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg"
                    >
                      <span className="text-sm text-zinc-900 dark:text-zinc-50">{dish}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDrinksDish(dish)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {settings.trackedDishes.drinks.length === 0 && (
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
                      {t('profile.reportSettings.noDrinksDishesSelected')}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-zinc-800 border-t border-zinc-200 dark:border-zinc-700 px-6 py-4">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportSettingsModal;

